import { useEffect, useMemo, useState } from "react"
import { Modal, ModalId, O } from "../../../../../features/ui"
import { describeHelpers } from "../../../../../features/data-types"
import { firstItem } from "../../../../../utils/listUtils"
import { Roadmap, useRoadmapLoaders } from "../../../../logistics"
import { Operation } from "../../../../operations"
import { Delivery, DeliveryOrder } from "../../../state/types"
import { deliveryHelpers } from "../../../hooks/deliveryHooks"
import { DeliveryStatsTable } from "../../delivery-stats-table/deliveryStatsTable"
import { useActions } from "./actions"

type Props = {
    delivery?: Delivery
    deliveryOrder?: DeliveryOrder
    operation?: Operation
    // Pages that render this modal more than once need a distinct id per instance: modals are opened by id
    modalId?: ModalId
    onAnnul?: () => void
}

export const AnnulDeliveryModal = (props: Props) => {
    const { delivery, deliveryOrder, operation, modalId = ModalId.ANNUL_DELIVERY, onAnnul } = props

    const [roadmap, setRoadmap] = useState<O<Roadmap>>()

    const { describeDeliveryNote } = deliveryHelpers
    const { describeCode } = describeHelpers

    const roadmapLoaders = useRoadmapLoaders()

    const { submitAnnulDelivery } = useActions()

    // A delivery note assigned to a roadmap is removed from it when annulled, so the user is warned first
    useEffect(() => {
        setRoadmap(undefined)
        if (delivery) {
            const { loadDeliveriesRoadmapsLinks, loadRoadmap } = roadmapLoaders()
            loadDeliveriesRoadmapsLinks([delivery.id], true).then(async deliveryRoadmaps => {
                const deliveryRoadmap = firstItem(deliveryRoadmaps)
                setRoadmap(deliveryRoadmap ? await loadRoadmap(deliveryRoadmap.roadmapId) : undefined)
            })
        }
    }, [delivery?.id])

    const dispatchedItems = useMemo(
        () => (delivery?.items || []).filter(item => item.quantity - item.deliveredQuantity - item.notDeliveredQuantity > 0),
        [delivery?.items]
    )

    const rows = useMemo(() => dispatchedItems.map(item => ({
        itemId: item.itemId,
        dispatched: item.quantity - item.deliveredQuantity - item.notDeliveredQuantity
    })), [dispatchedItems])

    const renderBody = () => (
        <>
            <p>
                {delivery && `Está por anular el ${describeDeliveryNote(delivery, deliveryOrder)}. `}
                Los productos despachados listados a continuación volverán al estado pendiente
                {' '}en la Orden de Entrega. Esta acción es irreversible.
            </p>
            {roadmap && (
                <p>
                    <strong>{`Este remito está asignado a la Hoja de Ruta ${describeCode(roadmap)}.`}</strong>
                    {' '}Al anularlo se va a quitar de la hoja de ruta y de su preparación de envío.
                </p>
            )}
            {rows.length > 0 && operation && (
                <DeliveryStatsTable
                    deliveryMethod={delivery!.method}
                    items={rows}
                    operation={operation}
                    title="Productos"
                />
            )}
        </>
    )

    const onAnnulDelivery = async () => {
        if (delivery) {
            await submitAnnulDelivery(delivery.id)
            onAnnul && onAnnul()
        }
    }

    return (
        <Modal
            modalId={modalId}
            title="Anular Entrega?"
            submitLabel="Anular"
            submitKind="secondary"
            widthSize="m"
            onSubmit={onAnnulDelivery}
        >
            {renderBody()}
        </Modal>
    )
}
